"use client";

import { getAuthErrorMessage } from "@saas/auth/client";
import { AuthHeading } from "@saas/shared/components/AuthWrapper";
import { useRouter } from "@shared/hooks/router";
import { Button } from "@ui/components/button";
import { Field, FieldDescription, FieldLabel } from "@ui/components/field";
import { DisabledReason } from "@ui/components/info-tip";
import { Input } from "@ui/components/input";
import { PasswordInput } from "@ui/components/password-input";
import { Spinner } from "@ui/components/spinner";
import { type FormEvent, useState } from "react";
import { signupWithVerifiedEmailFn } from "~/server/auth";
import { AuthError } from "../AuthNotice";
import {
	isPasswordStrongEnough,
	PasswordRulesChecklist,
} from "./PasswordRulesChecklist";

interface SignupStepPasswordProps {
	email: string;
	invitationId?: string;
}

export function SignupStepPassword({
	email,
	invitationId,
}: SignupStepPasswordProps) {
	const router = useRouter();
	const [name, setName] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	const ready = name.trim().length >= 2 && isPasswordStrongEnough(password);

	const submit = async (event: FormEvent) => {
		event.preventDefault();
		if (!ready || submitting) {
			return;
		}
		setError(null);
		setSubmitting(true);
		try {
			const result = await signupWithVerifiedEmailFn({
				data: { email, password, name: name.trim() },
			});
			if (!result.success) {
				setError(
					result.error === "VERIFICATION_EXPIRED" ||
						result.error === "EMAIL_NOT_VERIFIED"
						? "Your email check expired. Go back and send a new code."
						: getAuthErrorMessage(result.error),
				);
				setSubmitting(false);
				return;
			}
			router.push(
				invitationId
					? `/organization-invitation/${invitationId}`
					: "/app",
			);
		} catch {
			setError("Something went wrong creating your account. Try again.");
			setSubmitting(false);
		}
	};

	return (
		<div className="flex flex-col">
			<AuthHeading className="mb-6" title="Set up your account" />
			<form className="flex flex-col gap-5" onSubmit={submit} noValidate>
				<AuthError message={error} />
				<Field>
					<FieldLabel htmlFor="name">Full name</FieldLabel>
					<Input
						id="name"
						value={name}
						onChange={(event) => setName(event.target.value)}
						autoComplete="name"
						autoFocus
						maxLength={80}
						placeholder="Rana Haddad"
					/>
				</Field>
				<Field>
					<FieldLabel htmlFor="new-password">Password</FieldLabel>
					<PasswordInput
						id="new-password"
						value={password}
						onChange={setPassword}
						autoComplete="new-password"
					/>
					<PasswordRulesChecklist password={password} />
					<FieldDescription className="sr-only">
						Use at least the rules listed above.
					</FieldDescription>
				</Field>
				<DisabledReason
					reason={
						!ready &&
						!submitting &&
						"Add your name and a password that meets every rule"
					}
				>
					<Button
						type="submit"
						size="lg"
						className="w-full"
						disabled={!ready || submitting}
					>
						{submitting ? (
							<Spinner data-icon="inline-start" />
						) : null}
						Create account
					</Button>
				</DisabledReason>
			</form>
		</div>
	);
}
